import { useEffect, useRef, useState } from "react";
import type { Gif } from "../data/content.ts";
import { Icon } from "../ui/Icon.tsx";
import { gifs } from "./klipy.ts";
import styles from "./GifPicker.module.css";

/** The GIF search above the composer, like the app's picker; "Powered by KLIPY" as KLIPY asks. */
export function GifPicker({ onPick, onClose }: { onPick: (gif: Gif, query: string) => void; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<Gif[]>([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [error, setError] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  // 350 ms after the last keystroke, like the app.
  useEffect(() => {
    const abort = new AbortController();
    const timer = setTimeout(() => {
      gifs(query, 1, abort.signal).then(
        (p) => {
          setItems(p.gifs);
          setPage(1);
          setHasNext(p.hasNext);
          setError(false);
        },
        () => !abort.signal.aborted && setError(true),
      );
    }, 350);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [query]);

  async function more() {
    const next = await gifs(query, page + 1).catch(() => null);
    if (!next) return;
    setItems((current) => {
      const seen = new Set(current.map((g) => g.slug));
      return [...current, ...next.gifs.filter((g) => !seen.has(g.slug))];
    });
    setPage(next.page);
    setHasNext(next.hasNext);
  }

  return (
    <div className={styles.picker} role="dialog" aria-label="GIF auswählen" onKeyDown={(e) => e.key === "Escape" && onClose()}>
      <div className={styles.head}>
        <Icon name="search" />
        <input ref={input} placeholder="GIFs suchen" value={query} onChange={(e) => setQuery(e.target.value)} />
        <button onClick={onClose} aria-label="Schließen">
          <Icon name="close" />
        </button>
      </div>
      <div className={styles.grid}>
        {items.map((g) => (
          <button key={g.slug} className={styles.item} onClick={() => onPick(g, query)} title={g.title}>
            <img src={g.preview ?? g.url} alt={g.title} loading="lazy" style={{ aspectRatio: `${g.w} / ${g.h}` }} />
          </button>
        ))}
        {error && <p className={styles.note}>GIFs lassen sich gerade nicht laden.</p>}
        {!error && items.length === 0 && <p className={styles.note}>{query ? "Nichts gefunden." : "Wird geladen …"}</p>}
      </div>
      <div className={styles.foot}>
        {hasNext && (
          <button className={styles.more} onClick={() => void more()}>
            Mehr
          </button>
        )}
        <span>Powered by KLIPY</span>
      </div>
    </div>
  );
}
